import React, { useEffect, useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Header } from '@/components/layout/Header';
import { InboxHero } from '@/components/mail/InboxHero';
import { EmailTable } from '@/components/mail/EmailTable';
import { InfoSection } from '@/components/mail/InfoSection';
import { EmailDetailModal } from '@/components/mail/EmailDetailModal';
import { SetupModal } from '@/components/mail/SetupModal';
import { api } from '@/lib/api-client';
import type { Inbox, EmailMessage } from '@shared/types';
import { toast } from 'sonner';
export function HomePage() {
  const queryClient = useQueryClient();
  const [activeInboxId, setActiveInboxId] = useState<string | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null);
  const [isSetupOpen, setIsSetupOpen] = useState(false);
  const { data: inboxesResponse, isLoading: inboxesLoading } = useQuery<{ items: Inbox[] }>({
    queryKey: ['inboxes'],
    queryFn: () => api<{ items: Inbox[] }>('/api/inboxes'),
    staleTime: 60000,
  });
  const activeInbox = useMemo(() => {
    if (!inboxesResponse?.items) return null;
    if (activeInboxId) return inboxesResponse.items.find(i => i.id === activeInboxId) || inboxesResponse.items[0];
    return inboxesResponse.items[0] || null;
  }, [inboxesResponse?.items, activeInboxId]);
  useEffect(() => {
    if (activeInbox && !activeInboxId) {
      setActiveInboxId(activeInbox.id);
    }
  }, [activeInbox, activeInboxId]);
  const { data: emailsData, isLoading: emailsLoading, refetch: refetchEmails } = useQuery<EmailMessage[]>({
    queryKey: ['emails', activeInbox?.id],
    queryFn: () => api<EmailMessage[]>(`/api/inboxes/${activeInbox?.id}/emails`),
    enabled: !!activeInbox?.id,
    refetchInterval: 15000, // Poll every 15s for new messages
  });
  const emails = emailsData ?? [];
  const createInboxMutation = useMutation({
    mutationFn: (data?: { domain?: string, prefix?: string }) =>
      api<Inbox>('/api/inboxes', { method: 'POST', body: JSON.stringify(data || {}) }),
    onSuccess: (newInbox) => {
      queryClient.invalidateQueries({ queryKey: ['inboxes'] });
      setActiveInboxId(newInbox.id);
      toast.success('Hộp thư tạm thời mới đã được tạo thành công!');
    },
    onError: () => toast.error('Không thể tạo hộp thư mới.')
  });
  const deleteInboxMutation = useMutation({
    mutationFn: (id: string) => api(`/api/inboxes/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inboxes'] });
      setActiveInboxId(null);
      toast.info('Đã xóa hộp thư.');
    }
  });
  const deleteEmailMutation = useMutation({
    mutationFn: (id: string) => api(`/api/emails/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails', activeInbox?.id] });
      toast.success('Đã xóa email.');
    }
  });
  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary/30">
      <Header onOpenSetup={() => setIsSetupOpen(true)} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-8 md:py-10 lg:py-12">
          <InboxHero
            address={activeInbox?.address || ""}
            isLoading={inboxesLoading || createInboxMutation.isPending}
            onNew={() => createInboxMutation.mutate(undefined)}
            onRefresh={() => refetchEmails()}
            onDelete={() => activeInbox && deleteInboxMutation.mutate(activeInbox.id)}
            onOpenSetup={() => setIsSetupOpen(true)}
          />
          <div className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                Email gần đây
                <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary/20 text-primary text-xs font-bold">
                  {emails.length}
                </span>
              </h2>
              {emailsLoading && <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />}
            </div>
            <EmailTable
              emails={emails}
              isLoading={emailsLoading}
              onSelect={(email) => setSelectedEmail(email)}
              onDelete={(id) => deleteEmailMutation.mutate(id)}
            />
          </div>
          <InfoSection />
        </div>
      </main>
      <EmailDetailModal
        email={selectedEmail}
        onClose={() => setSelectedEmail(null)}
        onDelete={(id) => deleteEmailMutation.mutate(id)}
      />
      <SetupModal
        isOpen={isSetupOpen}
        onClose={() => setIsSetupOpen(false)}
      />
      <footer className="border-t border-white/5 py-12 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-muted-foreground text-sm">
          <p>© {new Date().getFullYear()} CLOUDTEMPMAIL. Bảo vệ quyền riêng tư của bạn ngay lập tức.</p>
        </div>
      </footer>
    </div>
  );
}